<template>
  <!-- If there are no locations, payments or terminals -->
  <v-skeleton-loader
    v-if="pageLoading"
    class="mx-auto border mt-5"
    type="actions, card, article, text"
  />
  <v-row justify="center" class="my-13" v-if="allEmpty() && !pageLoading">
    <v-col v-if="emptyLocation()">
      <add-location-card />
    </v-col>
    <v-col v-else>
      <add-terminal-landing-card :locations="locations" />
    </v-col>
  </v-row>

  <!-- Conditional rendering to show cards or empty components-->
  <v-row justify="center" class="mx-5 mt-5 dashboard-content" v-else>
    <v-col cols="12" md="7">
      <create-payment-card v-if="!emptyLocation() && emptyPayments() && !pageLoading" />
      <recent-payments-card v-if="!emptyPayments() && !pageLoading" />
    </v-col>
    <v-divider
      v-if="showEmptyPaymentsDivider()"
      vertical
      color="grey-lighten-2"
      class="d-none d-md-flex"
    />
    <v-col cols="12" md="4" v-if="!pageLoading">
      <v-skeleton-loader
        v-if="useLocationStore().loading"
        class="mx-auto border mt-5"
        type="actions, card, article, text"
      />
      <add-location-card v-if="!useLocationStore().loading && emptyLocation()" />
      <add-terminal-card
        v-if="!useLocationStore().loading && !emptyLocation() && emptyFavTerminals()"
        @updated="updateFavTerminals()"
      />
      <favorite-terminals-card
        v-if="!emptyFavTerminals() && !emptyLocation()"
        :favorite-terminals="favReaders"
        @updated="updateFavTerminals()"
      />
    </v-col>
  </v-row>

  <!-- Help Section (always visible) -->
  <!-- TODO we hide for now requested in https://airtable.com/appIVrmkVdk6RBsvw/tblLgZImNUhfNdOvV/viwR28glRZqklNbFi/recqbg6rQ0e4n9jzY?blocks=hide -->
  <v-row v-if="false" justify="center" class="mx-5">
    <v-col cols="12">
      <help-section-card />
    </v-col>
  </v-row>
</template>

<script lang="ts" setup>
import FavoriteTerminalsCard from "@/dashboard/components/dashboard/FavoriteTerminalsCard.vue";
import RecentPaymentsCard from "@/dashboard/components/dashboard/RecentPaymentsCard.vue";
import HelpSectionCard from "@/dashboard/components/dashboard/HelpSectionCard.vue";
import AddLocationCard from "@/dashboard/components/dashboard/AddLocationCard.vue";
import CreatePaymentCard from "@/dashboard/components/dashboard/CreatePaymentCard.vue";
import AddTerminalCard from "@/dashboard/components/dashboard/AddTerminalCard.vue";
import AddTerminalLandingCard from "@/dashboard/components/dashboard/AddTerminalLandingCard.vue";
import { useLocationStore } from "@/terminals/stores/locations";
import { usePaymentStore } from "@/payments/stores/payments";
import { useReaderStore } from "@/terminals/stores/readers";
import { onMounted, ref } from "vue";
import type { LocationSummary } from "@/terminals/types/locations";
import type { FavoriteTerminal } from "@/dashboard/types/dashboard";

const { fetchFavReader } = useReaderStore();
const { fetchPayments } = usePaymentStore();
const locations = ref<LocationSummary[]>([]);
const favReaders = ref<FavoriteTerminal[]>([]);
const pageLoading = ref(false);

const setActiveFavoriteReaders = (readers: unknown) => {
  favReaders.value = Array.isArray(readers)
    ? (readers as FavoriteTerminal[]).filter((terminal) => terminal.active === true)
    : [];
};

onMounted(() => {
  pageLoading.value = true;
  useReaderStore().$reset();
  usePaymentStore().$reset();
  useLocationStore().$reset();
  Promise.all([
    fetchPayments({ params: { limit: 10 } }),
    useLocationStore()
      .listLocations(false)
      .then((res) => (locations.value = res ?? [])),
    fetchFavReader().then(setActiveFavoriteReaders),
  ])
    .catch((error) => {
      error.value = error;
    })
    .finally(() => {
      pageLoading.value = false;
    });
});

const updateFavTerminals = async () => {
  const allFavoriteTerminals = await useReaderStore().fetchFavReader();
  setActiveFavoriteReaders(allFavoriteTerminals);
};

const allEmpty = () => {
  return locations?.value?.length === 0 && usePaymentStore().payments?.length === 0;
};

const emptyLocation = () => {
  return locations?.value?.length === 0;
};

const emptyPayments = () => {
  return usePaymentStore().payments?.length === 0;
};

const emptyFavTerminals = () => {
  return favReaders?.value?.length === 0;
};

const showEmptyPaymentsDivider = () => {
  return emptyPayments() && !emptyLocation();
};
</script>
